import type { ReactNode } from "react";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import {
  PREFERENCE_CATEGORIES,
  preferenceFor,
  useNotificationPreferences,
  useUpdateNotificationPreference,
  type PreferenceCategory
} from "../data/notificationPreferences";
import { BoxState } from "../ui/BoxState";
import { Panel } from "../ui/Panel";
import "./clientSettings/settings.css";

/**
 * Vilka notiser ni får: the last screen in Batch D, and deliberately the last.
 *
 * `docs/pilot-readiness.md` recorded why it could not be built earlier: "A preferences panel today
 * would offer a client a switch that turns off mail nobody sends. That is the same failure as a
 * placeholder page with build vocabulary on it, one layer further in: a control that lies about what
 * it controls. The preferences arrive with the delivery they configure." Both channels exist now.
 *
 * **In-app has no switch, and the absence is stated rather than left to be noticed.** plan.md section
 * 5.7 makes it the permanent base channel, `public.notification_preferences` has no column for it,
 * and a disabled checkbox would suggest the setting exists somewhere a person might go looking for
 * it.
 *
 * Security events have no row either. The check constraint on that table permits only the four below,
 * because "Password changed" and "MFA enabled, disabled or changed" are mandatory on both in-app and
 * email and cannot be switched off at all.
 *
 * **"Vad du får", beside the device, since the second build round of 2026-09-24**
 * (`prototype/kund/pages.js`, the `notiser` section). The table shares a screen with Den här enheten
 * now, so the note that sent a reader to the device panel "ovan" had its "ovan" back and was no
 * longer needed; it went with the lead under design.md section 3's third rule. What the two said that
 * a person needs is the heading's one line: the setting is the account's on every device, in-app has
 * no switch, and security notices always go.
 */

const CATEGORY_TEXT: Record<PreferenceCategory, { title: string; body: string }> = {
  chat_reply: {
    title: "Svar i chatten",
    body: "När vi svarar på ett meddelande från er."
  },
  request_update: {
    title: "Ärenden",
    body: "När ett ärende byter status, väntar på er eller blir klart."
  },
  outage: {
    title: "Driftstörningar",
    body: "När vi bekräftar ett avbrott och när det är löst."
  },
  document: {
    title: "Dokument",
    body: "När ett nytt dokument eller en ny version läggs upp."
  },
  report: {
    title: "Månadsrapporter",
    body: "När månadens rapport finns, den femte varje månad."
  }
};

const HINT =
  "Gäller ditt konto och alla dina enheter. Notiser i portalen kan inte stängas av. Säkerhetshändelser skickas alltid.";

export function NotificationPreferencesPanel() {
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const preferences = useNotificationPreferences(tenantId);
  const update = useUpdateNotificationPreference();

  const frame = (children: ReactNode) => (
    <Panel title="Vad du får" titleId="preferences-title" hint={HINT} flush testId="preferences-panel">
      {children}
    </Panel>
  );

  // The identity first, and only then the refusal. Until 2026-09-27 a missing `tenantId` meant "not
  // yours" whether or not the identity had arrived, so every client saw "Den här inställningen är inte
  // din" for the moment the identity took to load on a fresh page. A repeated run of
  // `tests/e2e/settings.spec.ts` caught the frame.
  if (identity.isLoading) {
    return frame(<BoxState kind="loading" compact title="Hämtar dina inställningar" />);
  }

  if (identity.isError) {
    return frame(
      <BoxState
        kind="error"
        compact
        title="Inställningarna gick inte att hämta"
        body="Ingenting har ändrats. Försök igen."
        onRetry={() => void identity.refetch()}
      />
    );
  }

  if (!tenantId) {
    return frame(
      <BoxState
        kind="denied"
        compact
        title="Den här inställningen är inte din"
        body="Kontot hör inte till någon kund i portalen."
      />
    );
  }

  if (preferences.isLoading) {
    return frame(<BoxState kind="loading" compact title="Hämtar dina inställningar" />);
  }

  if (preferences.isError) {
    return frame(
      <BoxState
        kind="error"
        compact
        title="Inställningarna gick inte att hämta"
        body="Ingenting har ändrats. Försök igen."
        onRetry={() => void preferences.refetch()}
      />
    );
  }

  function change(category: PreferenceCategory, channel: "push" | "email", next: boolean) {
    const current = preferenceFor(preferences.data, category);

    update.mutate({
      tenantId: tenantId as string,
      category,
      // Both are sent because the row holds both. Sending only the changed one would mean reading
      // the other back first and writing a value nobody looked at.
      pushEnabled: channel === "push" ? next : current.pushEnabled,
      emailEnabled: channel === "email" ? next : current.emailEnabled
    });
  }

  return frame(
    <>
      {update.isError ? (
        <p className="form-error k-set-inset" role="alert">
          {update.error?.message ?? "Inställningen kunde inte sparas. Försök igen."}
        </p>
      ) : null}

      {/*
        No box is disabled while a write is in flight, and that changed with the optimistic update in
        `useUpdateNotificationPreference`.

        The blanket `disabled={update.isPending}` was there because `checked` came straight from the
        query cache, so a click showed no change at all until the row had been written and refetched,
        and the disabling was the only feedback that anything had happened. It answered the wrong
        question. It froze all eight boxes over one category's write, took away a second change the
        person had already decided to make, and on a slow connection left the whole table dead for as
        long as the call took.

        The box now shows the new value immediately, which is the feedback the disabling was standing
        in for. A second click during the wait is an ordinary further change: it queues its own write
        and its own rollback, and the invalidation on settle gives the server the last word on every
        row either way.
      */}
      <div className="table-wrap">
        <table className="table k-prefs">
          <thead>
            <tr>
              <th scope="col">Händelse</th>
              <th scope="col">E-post</th>
              <th scope="col">Avisering</th>
            </tr>
          </thead>
          <tbody>
            {PREFERENCE_CATEGORIES.map((category) => {
              const current = preferenceFor(preferences.data, category);
              const text = CATEGORY_TEXT[category];

              return (
                <tr key={category} data-testid={`preference-${category}`}>
                  <th scope="row">
                    <span>{text.title}</span>
                    <span className="cell-sub">{text.body}</span>
                  </th>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`E-post om ${text.title.toLowerCase()}`}
                      checked={current.emailEnabled}
                      onChange={(event) => change(category, "email", event.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Avisering om ${text.title.toLowerCase()}`}
                      checked={current.pushEnabled}
                      onChange={(event) => change(category, "push", event.target.checked)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
